import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

interface CreatePortfolioDialogProps {
  onCreatePortfolio: (name: string, type: string) => void;
  buttonClassName?: string;
  // Optional props for controlling the dialog externally
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // Optional prop to hide the trigger button (for controlled usage)
  hideTrigger?: boolean;
  // Optional defaults for name/type when opening
  defaultName?: string;
  defaultType?: string;
  // Optional: restrict selectable portfolio types
  allowedTypes?: string[];
}

export function CreatePortfolioDialog({ 
  onCreatePortfolio, 
  buttonClassName,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  hideTrigger = false,
  defaultName = "New Portfolio",
  defaultType = "model",
  allowedTypes,
}: CreatePortfolioDialogProps) {
  // Use internal state if no external state is provided
  const [internalOpen, setInternalOpen] = useState(false);
  const [portfolioName, setPortfolioName] = useState(defaultName);
  const [portfolioType, setPortfolioType] = useState(defaultType);
  
  // Use external or internal open state based on what's provided
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOnOpenChange || setInternalOpen;

  const TYPE_OPTIONS: Array<{ value: string; label: string }> = [
    { value: 'model', label: 'Model Portfolio' },
    { value: 'personal', label: 'Personal Portfolio' },
    { value: 'client', label: 'Client Portfolio' },
  ];
  const filteredOptions = Array.isArray(allowedTypes) && allowedTypes.length > 0
    ? TYPE_OPTIONS.filter(o => allowedTypes.includes(o.value))
    : TYPE_OPTIONS;

  const handleCreate = () => {
    onCreatePortfolio(portfolioName, portfolioType);
    setOpen(false);
    // Reset form
    setPortfolioName(defaultName);
    setPortfolioType(defaultType);
  };

  return (
    <Dialog open={open} onOpenChange={(o)=>{ setOpen(o); if (o){ setPortfolioName(defaultName); setPortfolioType(defaultType);} }}>
      {!hideTrigger && (
        <DialogTrigger asChild>
        <Button size="sm" className={buttonClassName || "whitespace-nowrap"}>
          <Plus className="h-4 w-4" />
          New Portfolio
        </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create Portfolio</DialogTitle>
          <DialogDescription>
            Create a new portfolio to manage your investments.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
            <Label htmlFor="portfolio-name" className="sm:text-right font-medium">
              Name
            </Label>
            <Input
              id="portfolio-name"
              value={portfolioName}
              onChange={(e) => setPortfolioName(e.target.value)}
              className="sm:col-span-3 min-h-[44px]"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
            <Label htmlFor="portfolio-type" className="sm:text-right font-medium">
              Type
            </Label>
            <Select 
              value={portfolioType} 
              onValueChange={setPortfolioType}
            >
              <SelectTrigger id="portfolio-type" className="sm:col-span-3 min-h-[44px]">
                <SelectValue placeholder="Select portfolio type" />
              </SelectTrigger>
              <SelectContent className="z-50 bg-popover">
                {filteredOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
          <Button 
            variant="outline" 
            onClick={() => setOpen(false)}
            className="w-full sm:w-auto min-h-[44px]"
          >
            Cancel
          </Button>
          <Button onClick={handleCreate} className="w-full sm:w-auto min-h-[44px]">
            Create Portfolio
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
